import React, { useState, useEffect } from 'react';
import { Card, CardBody, Button, Chip, Progress, Textarea, useDisclosure, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@nextui-org/react';
import WorkflowService from '../services/WorkflowService';

/**
 * Simple Task Workflow View Component
 * Shows workflow status and allows actions
 */
const TaskWorkflowView = ({ task, onUpdate }) => {
  const [workflowStatus, setWorkflowStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [selectedAction, setSelectedAction] = useState(null);
  
  const { isOpen: isActionModalOpen, onOpen: onActionModalOpen, onClose: onActionModalClose } = useDisclosure();

  // Load workflow status when component mounts
  useEffect(() => {
    if (task?.id) {
      loadWorkflowStatus();
    }
  }, [task?.id]);

  const loadWorkflowStatus = async () => {
    try {
      setLoading(true);
      const status = await WorkflowService.getTaskWorkflowStatus(task.id);
      setWorkflowStatus(status);
    } catch (error) {
      console.error('Error loading workflow status:', error);
      setWorkflowStatus({ hasWorkflow: false, message: 'Error loading workflow status' });
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (action) => {
    setSelectedAction(action);
    setCommentText('');
    onActionModalOpen();
  };

  const submitAction = async () => {
    if (!selectedAction || !commentText.trim()) {
      alert('Please provide a comment');
      return;
    }

    try {
      setActionLoading(true);
      
      const result = await WorkflowService.processWorkflowAction(
        task.id, 
        selectedAction.buttonName, 
        'current-user', // TODO: Get from auth context
        commentText
      );

      if (result.success) {
        onActionModalClose();
        setCommentText('');
        setSelectedAction(null);
        
        // Reload workflow status
        await loadWorkflowStatus();
        
        // Notify parent
        if (onUpdate) {
          onUpdate(task.id, result);
        }
        
        alert('Action completed successfully!');
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border border-slate-200">
        <CardBody className="p-4 text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto mb-2"></div>
          <p className="text-sm text-slate-600">Loading workflow status...</p>
        </CardBody>
      </Card>
    );
  }

  if (!workflowStatus?.hasWorkflow) {
    return (
      <Card className="border border-slate-200">
        <CardBody className="p-4 text-center">
          <p className="text-sm text-slate-500">{workflowStatus?.message || 'No workflow assigned'}</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Card className="border border-slate-200">
        <CardBody className="p-4 space-y-4">
          {/* Workflow Header */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-slate-900">Workflow: {workflowStatus.workflowName}</h4>
              <p className="text-sm text-slate-600">
                Step {workflowStatus.currentStepNumber} of {workflowStatus.totalSteps}
              </p>
            </div>
            <Chip size="sm" color="primary" variant="flat">
              {workflowStatus.progress}% Complete
            </Chip>
          </div>

          {/* Progress Bar */}
          <div>
            <Progress
              size="sm"
              value={workflowStatus.progress}
              color="success"
              className="w-full"
            />
          </div>

          {/* Current Step */}
          <div className="bg-slate-50 p-3 rounded-lg">
            <h5 className="font-medium text-slate-900 mb-2">Current Step</h5>
            <p className="text-sm text-slate-700">{workflowStatus.currentStep.name}</p>
          </div>

          {/* Available Actions */}
          {workflowStatus.availableActions && workflowStatus.availableActions.length > 0 && (
            <div>
              <h5 className="font-medium text-slate-900 mb-2">Available Actions</h5>
              <div className="flex flex-wrap gap-2">
                {workflowStatus.availableActions.map((action, index) => (
                  <Button
                    key={index}
                    size="sm"
                    color={action.buttonName.toLowerCase().includes('approve') ? 'success' : 
                           action.buttonName.toLowerCase().includes('reject') ? 'danger' : 'primary'}
                    variant="flat"
                    onPress={() => handleAction(action)}
                  >
                    {action.buttonName}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Action Modal */}
      <Modal isOpen={isActionModalOpen} onClose={onActionModalClose} size="md">
        <ModalContent>
          <ModalHeader>
            <h3 className="text-lg font-semibold">
              Action: {selectedAction?.buttonName}
            </h3>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Comments (Required)
                </label>
                <Textarea
                  placeholder="Provide your feedback or reasoning..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  minRows={3}
                  isRequired
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              variant="flat"
              onPress={onActionModalClose}
              isDisabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              color="success"
              onPress={submitAction}
              isDisabled={actionLoading || !commentText.trim()}
              isLoading={actionLoading}
            >
              Submit Action
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default TaskWorkflowView;

